import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, Mail } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { forgotPassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await forgotPassword(email);
      setSubmitted(true);
      toast.success('If an account exists, a reset link has been sent.');
      
      // Dev mode - log reset link
      if (result.dev_mode && result.reset_link) {
        console.log('Dev reset link:', result.reset_link);
      }
    } catch (error) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md text-center"
        >
          <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
            <div className="w-16 h-16 bg-[#00B4B4]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="h-8 w-8 text-[#00B4B4]" />
            </div>
            
            <h1 className="text-2xl font-semibold text-[#1A365D]">Check Your Email</h1>
            
            <p className="text-slate-600 mt-4">
              If an account exists for <span className="font-medium text-[#1A365D]">{email}</span>, 
              we've sent instructions to reset your password.
            </p>
            
            <Link to="/login">
              <Button className="mt-8 bg-[#00B4B4] text-white">
                Back to Sign In
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white p-8 rounded-xl border border-slate-200 shadow-sm">
          <Link 
            to="/login"
            className="inline-flex items-center text-sm text-slate-600 hover:text-[#00B4B4] mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Sign In
          </Link>
          
          <h1 className="text-2xl font-semibold text-[#1A365D]">Reset Password</h1>
          <p className="text-slate-600 mt-2">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5 mt-6" data-testid="forgot-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                type="email"
                placeholder="john@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
              />
            </div>
            
            <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
              <Button
                type="submit"
                className="w-full bg-[linear-gradient(160deg,_#00B4B4,_#2BC3C3)] text-white py-3"
                disabled={loading}
                data-testid="forgot-form-submit-button"
              >
                {loading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </motion.div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
